/**
 * Перерахування податків з нарахувань — список (канон Норми #29, 2026-08-23):
 * InfiniteDocGrid + серверні фільтри орг/період + сортування.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { taxRemittances, type TaxRemittanceListItem } from '@/api/tax_remittances';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import PeriodFilter, { useHrPeriodFilter, MONTHS_UK } from '@/shared/components/PeriodFilter';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (p: { value?: string | null }) => p.value != null ? fmt2(p.value) : '';

export default function TaxRemittancesPage() {
  const navigate = useNavigate();
  const [orgId, setOrgId] = useOrgFilter();
  const per = useHrPeriodFilter();

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m = new Map<string, string>();
    orgList.data?.items.forEach((o) => m.set(o.id, o.name));
    return m;
  }, [orgList.data]);

  const { datasource, total } = useInfiniteGridDatasource<TaxRemittanceListItem>(
    (limit, offset, sort) => taxRemittances.list({
      organization_id: orgId || undefined,
      ...per.params,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, ...per.key],
  );

  const cols = useMemo<ColDef<TaxRemittanceListItem>[]>(() => [
    { field: 'document_number', headerName: '№', width: 130 },
    {
      field: 'organization_id', headerName: 'Організація', width: 200,
      valueFormatter: (p) => p.value ? (orgName.get(p.value) ?? '—') : '',
    },
    {
      field: 'document_date', headerName: 'Дата', width: 120,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleDateString('uk-UA') : '',
    },
    {
      colId: 'period', headerName: 'Період', width: 140,
      valueGetter: (p) => p.data ? `${MONTHS_UK[p.data.period_month]} ${p.data.period_year}` : '',
    },
    { field: 'total_pdfo', headerName: 'ПДФО', width: 110, cellStyle: { textAlign: 'right' }, valueFormatter: money },
    { field: 'total_wz', headerName: 'ВЗ', width: 110, cellStyle: { textAlign: 'right' }, valueFormatter: money },
    { field: 'total_esv', headerName: 'ЄСВ', width: 110, cellStyle: { textAlign: 'right' }, valueFormatter: money },
    {
      field: 'posted', headerName: 'Проведено', width: 110,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [orgName]);

  return (
    <div className="card">
      <div className="toolbar with-filters">
        <h2 style={{ margin: 0 }}>Перерахування податків з нарахувань<TitleTotal total={total} /></h2>
        <button className="primary" onClick={() => navigate('/tax-remittances/new')}>
          + Нове перерахування
        </button>
      </div>
      <div className="filters" style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0 0 0.6rem', alignItems: 'center' }}>
        <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ maxWidth: 260 }}>
          <option value="">Усі організації</option>
          {orgList.data?.items.map((o) => <option key={o.id} value={o.id}>{o.name} — {o.code}</option>)}
        </select>
        <PeriodFilter {...per.bind} />
      </div>
      <InfiniteDocGrid<TaxRemittanceListItem>
        copyRow={(r) => navigate(`/tax-remittances/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => taxRemittances.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="tax-remittances"
        onRowDoubleClicked={(r) => navigate(`/tax-remittances/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
